import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import VideoCard from '@/components/feature/VideoCard';
import FilterBar from '@/components/feature/FilterBar';
import { categories } from '@/mocks/categories';
import { videos } from '@/mocks/videos';
import { filterVideos, sortVideos, type SortValue } from '@/utils/video';

const PAGE_SIZE = 8;

export default function Category() {
  const { type } = useParams<{ type: string }>();
  const category = categories.find((c) => c.type === type);

  const [region, setRegion] = useState('全部');
  const [year, setYear] = useState('全部');
  const [sort, setSort] = useState<SortValue>('latest');
  const [visible, setVisible] = useState(PAGE_SIZE);

  const baseList = useMemo(
    () => videos.filter((v) => v.category === type),
    [type]
  );

  const filtered = useMemo(() => {
    const result = sortVideos(filterVideos(baseList, { region, year }), sort);
    return result;
  }, [baseList, region, year, sort]);

  const shown = filtered.slice(0, visible);

  const resetFilters = () => {
    setRegion('全部');
    setYear('全部');
    setSort('latest');
    setVisible(PAGE_SIZE);
  };

  const changeRegion = (v: string) => {
    setRegion(v);
    setVisible(PAGE_SIZE);
  };
  const changeYear = (v: string) => {
    setYear(v);
    setVisible(PAGE_SIZE);
  };
  const changeSort = (v: SortValue) => {
    setSort(v);
    setVisible(PAGE_SIZE);
  };

  if (!category) {
    return (
      <div className="min-h-screen bg-background-50 flex flex-col">
        <Navbar />
        <main className="flex-1 flex flex-col items-center justify-center px-4 py-24 text-center">
          <span className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-5">
            <i className="ri-emotion-sad-line text-3xl"></i>
          </span>
          <h1 className="font-heading text-2xl font-extrabold text-foreground-950">分类不存在</h1>
          <p className="mt-3 text-sm text-foreground-500">该分类页面暂未开通，请返回首页浏览其他内容。</p>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-home-4-line"></i>
            返回首页
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50 flex flex-col">
      <Navbar />
      <main className="flex-1">
        <div className="max-w-[1440px] mx-auto px-4 md:px-8 py-6">
          <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-5 flex-wrap">
            <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">
              首页
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-900 font-medium">{category.label}</span>
          </nav>

          <div className="rounded-lg bg-background-50 border border-background-200 p-6 md:p-8 mb-6">
            <h1 className="font-heading text-2xl md:text-3xl font-extrabold text-foreground-950 flex items-center gap-3">
              <span className="w-11 h-11 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                <i className={`${category.icon} text-2xl`}></i>
              </span>
              {category.label}
            </h1>
            <p className="mt-3 text-sm md:text-base text-foreground-600 leading-relaxed max-w-3xl">
              {category.desc}
            </p>
            <p className="mt-2 text-sm text-foreground-500">
              共 <strong className="text-primary-600">{baseList.length}</strong> 部内容
            </p>
          </div>

          <div className="mb-6">
            <FilterBar
              region={region}
              year={year}
              sort={sort}
              onRegionChange={changeRegion}
              onYearChange={changeYear}
              onSortChange={changeSort}
            />
          </div>

          {shown.length > 0 ? (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                {shown.map((video) => (
                  <VideoCard key={video.id} video={video} />
                ))}
              </div>

              {visible < filtered.length && (
                <div className="flex justify-center mt-10">
                  <button
                    type="button"
                    onClick={() => setVisible((v) => v + PAGE_SIZE)}
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-background-100 text-foreground-800 text-sm font-medium hover:bg-primary-500 hover:text-background-50 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    加载更多
                    <i className="ri-arrow-down-line"></i>
                  </button>
                </div>
              )}
              {visible >= filtered.length && (
                <p className="text-center text-sm text-foreground-400 mt-10">已经到底啦，换个筛选条件看看吧</p>
              )}
            </>
          ) : (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <span className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-5">
                <i className="ri-file-search-line text-3xl"></i>
              </span>
              <h2 className="font-heading text-lg font-bold text-foreground-950">没有符合条件的视频</h2>
              <p className="mt-2 text-sm text-foreground-500">试试调整筛选条件，或重置后重新浏览。</p>
              <button
                type="button"
                onClick={resetFilters}
                className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-refresh-line"></i>
                重置筛选
              </button>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}